import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../utils/queryKeys';
import {type TypedTranslation} from '../../i18n';
import {IPresentation} from '../../incase-domain/presentation';
import {startPresUpdateSpinnerAction, stopPresUpdateSpinnerAction} from '../../store/actions/spinner';
import {importPresentation} from '../presentation';

export const IMPORT_PRESENTATION_KEY = ['importPresentation'];

function useImportPresentation(): UseMutationResult<IPresentation, unknown, {presentation: IPresentation}, void> {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const {t} = useTranslation();

    return useMutation(
        async (args: {presentation: IPresentation}) => {
            const presentation = JSON.parse(args.presentation as unknown as string) as IPresentation;
            if (presentation.slides.length === 0) {
                toast(t('apiResponses.failedImportNoSlides'));
                throw new Error('failedImportNoSlides');
            }
            return importPresentation(presentation);
        },
        {
            mutationKey: IMPORT_PRESENTATION_KEY,
            onMutate: () => {
                dispatch(startPresUpdateSpinnerAction());
            },
            onSettled: () => {
                dispatch(stopPresUpdateSpinnerAction());
            },
            onSuccess: () => {
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
            },
            onError: (error) => {
                if (axios.isAxiosError(error)) {
                    const errorData = (error as AxiosError<{message: string; error: string}>).response?.data;

                    if (errorData) {
                        if (errorData.message.includes('presTooLarge') || errorData.message.includes('maxPresNumberReached')) {
                            toast.error(t(errorData.message as TypedTranslation));
                        } else if (errorData.message.includes('failedImportNoSlides')) {
                            toast.error(t('apiResponses.failedImportNoSlides'));
                        } else {
                            toast.error(t('toasts.presentationImportFailed'));
                        }
                    }
                }
            },
        }
    );
}

export default useImportPresentation;
